import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import toast from 'react-hot-toast';
import { CONFIG } from "../../../../../../config";

const API_HOST_CRM = CONFIG.modules["crm"].host;

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const UploadExcelSheet = ({ onFileSelect, handleClose, setData }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    
    if (selectedFile) {
      // Check if file is Excel (.xlsx)
      if (!selectedFile.name.endsWith('.xlsx')) {
        alert('Please upload only Excel (.xlsx) files');
        return;
      }
      
      setFile(selectedFile);
      if (onFileSelect) {
        onFileSelect(selectedFile);
      }
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    if (onFileSelect) {
      onFileSelect(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(API_HOST_CRM + '/case/bulk-update', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 200) {
        toast.success('File uploaded and processed successfully!');
        handleRemoveFile();
        handleClose()
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.message || 'Failed to upload and process file');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {!file ? (
        <Button
          component="label"
          variant="contained"
          startIcon={<CloudUploadIcon />}
          sx={{ width: '100%', height: '100px' }}
        >
          Upload Excel File
          <VisuallyHiddenInput
            type="file"
            accept=".xlsx"
            onChange={handleFileChange}
          />
        </Button>
      ) : (
        <Paper
          elevation={3}
          sx={{
            p: 2,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold' }}>
              {file.name}
            </Typography>
            <IconButton onClick={handleRemoveFile} color="error" size="small">
              <DeleteIcon />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Size: {formatFileSize(file.size)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Type: {file.name.split('.').pop().toUpperCase()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              onClick={handleUpload}
              disabled={uploading}
              fullWidth
            >
              {uploading ? 'Uploading...' : 'Upload and Process'}
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default UploadExcelSheet;